import React, { useState, useEffect } from 'react';
import { useDeletionLogs } from '../hooks/useBillingSystem';
import { useBusinessHoursConfig } from '../hooks/useBusinessHours';

// ==================== SVG ICONS ====================

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
  </svg>
);

const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);

const FilterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
  </svg>
);

const ChartIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 20V10M12 20V4M6 20v-6"/>
  </svg>
);

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);


const ChevronLeftIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

const RefreshIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
  </svg>
);

const LinkIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);

// ==================== STYLES ====================

const styles = `
  .dc-container {
    padding: 0;
  }

  .dc-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
  }

  .dc-header h2 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: #1e3a8a;
  }

  .dc-header svg {
    color: #2563eb;
  }

  .dc-loading {
    padding: 2rem;
    text-align: center;
    color: #64748b;
    font-size: 0.8rem;
  }

  /* Card Base */
  .dc-card {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-left: 4px solid #2563eb;
    border-radius: 6px;
    padding: 12px 14px;
    margin-bottom: 12px;
  }

  .dc-card-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e2e8f0;
  }

  .dc-card-header h3 {
    margin: 0;
    font-size: 0.8rem;
    font-weight: 600;
    color: #1e40af;
  }

  .dc-card-header svg {
    color: #3b82f6;
  }

  /* Status Section */
  .dc-status-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
  }

  .dc-status-light {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .dc-status-light.green {
    background: #22c55e;
    box-shadow: 0 0 6px rgba(34, 197, 94, 0.5);
  }

  .dc-status-light.gray {
    background: #94a3b8;
  }

  .dc-status-text {
    font-size: 0.8rem;
    font-weight: 500;
    color: #1e3a8a;
    margin: 0;
  }

  .dc-status-details {
    font-size: 0.7rem;
    color: #64748b;
    margin: 2px 0 0 0;
  }

  .dc-config-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #e2e8f0;
  }

  @media (max-width: 640px) {
    .dc-config-grid {
      grid-template-columns: 1fr;
    }
  }

  .dc-config-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .dc-config-item .label {
    font-size: 0.65rem;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  .dc-config-item .value {
    font-size: 0.75rem;
    font-weight: 500;
    color: #1e3a8a;
  }

  .dc-config-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.7rem;
    color: #2563eb;
    text-decoration: none;
    font-weight: 500;
  }

  .dc-config-link:hover {
    text-decoration: underline;
  }

  /* Filter Section */
  .dc-filter-row {
    display: flex;
    align-items: flex-end;
    gap: 10px;
    flex-wrap: wrap;
  }

  .dc-form-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .dc-form-group label {
    font-size: 0.65rem;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  .dc-form-group input[type="date"] {
    padding: 6px 8px;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    font-size: 0.75rem;
    color: #1e3a8a;
    background: #fff;
    min-width: 140px;
  }

  .dc-form-group input[type="date"]:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
  }

  .dc-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .dc-btn-primary {
    background: #2563eb;
    color: #fff;
    border: none;
  }

  .dc-btn-primary:hover {
    background: #1e40af;
  }

  .dc-btn-secondary {
    background: #fff;
    color: #64748b;
    border: 1px solid #cbd5e1;
  }

  .dc-btn-secondary:hover {
    background: #f1f5f9;
    color: #1e3a8a;
  }

  /* Table */
  .dc-table-container {
    overflow-x: auto;
    margin: 0 -14px;
    padding: 0 14px;
  }

  .dc-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.75rem;
  }

  .dc-table th {
    background: #f1f5f9;
    color: #64748b;
    font-weight: 600;
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    padding: 8px 10px;
    text-align: left;
    border-bottom: 1px solid #e2e8f0;
  }

  .dc-table td {
    padding: 8px 10px;
    border-bottom: 1px solid #e2e8f0;
    color: #334155;
  }

  .dc-table tbody tr:hover {
    background: #f8fafc;
  }

  .dc-invoice-number {
    font-weight: 600;
    color: #1e40af;
  }

  .dc-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 0.65rem;
    font-weight: 500;
  }

  .dc-badge-auto {
    background: #dbeafe;
    color: #1e40af;
  }

  .dc-badge-manual {
    background: #fef3c7;
    color: #92400e;
  }

  .dc-empty-state {
    padding: 2rem;
    text-align: center;
    color: #64748b;
    font-size: 0.8rem;
  }

  /* Pagination */
  .dc-pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 10px;
    margin-top: 10px;
    border-top: 1px solid #e2e8f0;
  }

  .dc-btn-pagination {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 10px;
    background: #fff;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    font-size: 0.7rem;
    color: #1e40af;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .dc-btn-pagination:hover:not(:disabled) {
    background: #dbeafe;
    border-color: #2563eb;
  }

  .dc-btn-pagination:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .dc-pagination-info {
    font-size: 0.7rem;
    color: #64748b;
  }

  /* Summary Grid */
  .dc-summary-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
  }

  @media (max-width: 768px) {
    .dc-summary-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (max-width: 480px) {
    .dc-summary-grid {
      grid-template-columns: 1fr;
    }
  }

  .dc-summary-item {
    background: #f8fafc;
    border-radius: 4px;
    padding: 10px;
    text-align: center;
    border: 1px solid #e2e8f0;
  }

  .dc-summary-label {
    display: block;
    font-size: 0.65rem;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    margin-bottom: 4px;
  }

  .dc-summary-value {
    display: block;
    font-size: 0.9rem;
    font-weight: 700;
    color: #1e40af;
  }
`;

const ITEMS_PER_PAGE = 30;

export const DeletionControl: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const { logs, loading: logsLoading, refetch } = useDeletionLogs({
    limit: ITEMS_PER_PAGE,
    offset: (currentPage - 1) * ITEMS_PER_PAGE,
    startDate: startDate ? new Date(startDate) : undefined,
    endDate: endDate ? new Date(endDate) : undefined,
  });

  const { config: businessHoursConfig, loading: configLoading } = useBusinessHoursConfig();

  const handleFilter = () => {
    setCurrentPage(1);
    refetch();
  };

  const handleNextPage = () => {
    if (logs.length === ITEMS_PER_PAGE) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  useEffect(() => {
    refetch();
  }, [currentPage, refetch]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString('es-CO');
  };

  const isWithinWorkHours = () => {
    if (!businessHoursConfig) return false;

    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTime = currentHour * 60 + currentMinute;

    const startTime = businessHoursConfig.startHour * 60 + businessHoursConfig.startMinute;
    const endTime = businessHoursConfig.endHour * 60 + businessHoursConfig.endMinute;

    const dayOfWeek = now.getDay();
    const dayEnabled = [
      businessHoursConfig.sunday,
      businessHoursConfig.monday,
      businessHoursConfig.tuesday,
      businessHoursConfig.wednesday,
      businessHoursConfig.thursday,
      businessHoursConfig.friday,
      businessHoursConfig.saturday,
    ][dayOfWeek];

    if (!dayEnabled) return false;

    return currentTime >= startTime && currentTime <= endTime;
  };

  const getStatusColor = () => {
    if (!businessHoursConfig) return 'gray';
    return isWithinWorkHours() ? 'green' : 'gray';
  };

  const getStatusText = () => {
    if (!businessHoursConfig) return 'Cargando...';
    return isWithinWorkHours() ? 'Dentro de Horario Laboral' : 'Fuera de Horario Laboral';
  };

  const getDayName = (index: number) => {
    const days = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
    return days[index];
  };

  const getActiveDays = () => {
    if (!businessHoursConfig) return '';
    const days = [
      businessHoursConfig.sunday,
      businessHoursConfig.monday,
      businessHoursConfig.tuesday,
      businessHoursConfig.wednesday,
      businessHoursConfig.thursday,
      businessHoursConfig.friday,
      businessHoursConfig.saturday,
    ];
    return days
      .map((active, index) => (active ? getDayName(index) : null))
      .filter(Boolean)
      .join(', ');
  };

  const getTotalStats = () => {
    const totalAmount = logs.reduce((sum, log) => sum + log.amount, 0);
    const automaticCount = logs.filter(log => log.deletionType === 'AUTOMATIC').length;
    const manualCount = logs.filter(log => log.deletionType === 'MANUAL').length;
    return { totalAmount, automaticCount, manualCount, totalCount: logs.length };
  };

  const stats = getTotalStats();

  if (configLoading) {
    return (
      <div className="dc-container">
        <style>{styles}</style>
        <div className="dc-header">
          <TrashIcon />
          <h2>Historial de Eliminaciones</h2>
        </div>
        <div className="dc-loading">Cargando configuración...</div>
      </div>
    );
  }

  return (
    <div className="dc-container">
      <style>{styles}</style>

      <div className="dc-header">
        <TrashIcon />
        <h2>Historial de Eliminaciones</h2>
      </div>

      {/* Estado del Sistema */}
      <div className="dc-card">
        <div className="dc-card-header">
          <ClockIcon />
          <h3>Estado del Sistema</h3>
        </div>
        <div className="dc-status-row">
          <div className={`dc-status-light ${getStatusColor()}`}></div>
          <div>
            <p className="dc-status-text">{getStatusText()}</p>
            {businessHoursConfig && (
              <p className="dc-status-details">
                Horario: {String(businessHoursConfig.startHour).padStart(2, '0')}:
                {String(businessHoursConfig.startMinute).padStart(2, '0')} -{' '}
                {String(businessHoursConfig.endHour).padStart(2, '0')}:
                {String(businessHoursConfig.endMinute).padStart(2, '0')}
              </p>
            )}
          </div>
        </div>

        {businessHoursConfig && (
          <div className="dc-config-grid">
            <div className="dc-config-item">
              <span className="label">Días Activos</span>
              <span className="value">{getActiveDays()}</span>
            </div>
            <div className="dc-config-item">
              <span className="label">Horas/Día</span>
              <span className="value">{businessHoursConfig.totalHours} horas</span>
            </div>
            <div className="dc-config-item">
              <span className="label">Configuración</span>
              <a href="/horarios" className="dc-config-link">
                <LinkIcon /> Ver Horarios
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Filtros de Auditoría */}
      <div className="dc-card">
        <div className="dc-card-header">
          <FilterIcon />
          <h3>Filtros de Auditoría</h3>
        </div>
        <div className="dc-filter-row">
          <div className="dc-form-group">
            <label>Fecha Inicio</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="dc-form-group">
            <label>Fecha Fin</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <button className="dc-btn dc-btn-primary" onClick={handleFilter}>
            <FilterIcon /> Filtrar
          </button>
          <button
            className="dc-btn dc-btn-secondary"
            onClick={() => {
              setStartDate('');
              setEndDate('');
              setCurrentPage(1);
              refetch();
            }}
          >
            <RefreshIcon /> Limpiar
          </button>
        </div>
      </div>

      {/* Historial de Eliminaciones */}
      <div className="dc-card">
        <div className="dc-card-header">
          <CalendarIcon />
          <h3>Historial de Eliminaciones</h3>
        </div>
        {logsLoading ? (
          <div className="dc-loading">Cargando historial...</div>
        ) : logs.length === 0 && currentPage === 1 ? (
          <div className="dc-empty-state">No hay registros de eliminación</div>
        ) : (
          <>
            <div className="dc-table-container">
              <table className="dc-table">
                <thead>
                  <tr>
                    <th>Fecha/Hora</th>
                    <th>Factura</th>
                    <th>Monto</th>
                    <th>Tipo</th>
                    <th>Usuario</th>
                    <th>Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id}>
                      <td>{formatDate(log.deletedAt)}</td>
                      <td className="dc-invoice-number">{log.invoiceNumber}</td>
                      <td>{formatCurrency(log.amount)}</td>
                      <td>
                        <span
                          className={`dc-badge ${
                            log.deletionType === 'AUTOMATIC' ? 'dc-badge-auto' : 'dc-badge-manual'
                          }`}
                        >
                          {log.deletionType === 'AUTOMATIC' ? 'Automática' : 'Manual'}
                        </span>
                      </td>
                      <td>{log.deletedBy}</td>
                      <td>{log.reason || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginación */}
            <div className="dc-pagination">
              <button
                className="dc-btn-pagination"
                onClick={handlePreviousPage}
                disabled={currentPage === 1 || logsLoading}
              >
                <ChevronLeftIcon /> Anterior
              </button>
              <span className="dc-pagination-info">
                Página {currentPage} | {logs.length} registros
              </span>
              <button
                className="dc-btn-pagination"
                onClick={handleNextPage}
                disabled={logs.length < ITEMS_PER_PAGE || logsLoading}
              >
                Siguiente <ChevronRightIcon />
              </button>
            </div>
          </>
        )}
      </div>

      {/* Resumen de esta Página */}
      {logs.length > 0 && (
        <div className="dc-card">
          <div className="dc-card-header">
            <ChartIcon />
            <h3>Resumen de esta Página</h3>
          </div>
          <div className="dc-summary-grid">
            <div className="dc-summary-item">
              <span className="dc-summary-label">Registros</span>
              <span className="dc-summary-value">{stats.totalCount}</span>
            </div>
            <div className="dc-summary-item">
              <span className="dc-summary-label">Automáticas</span>
              <span className="dc-summary-value">{stats.automaticCount}</span>
            </div>
            <div className="dc-summary-item">
              <span className="dc-summary-label">Manuales</span>
              <span className="dc-summary-value">{stats.manualCount}</span>
            </div>
            <div className="dc-summary-item">
              <span className="dc-summary-label">Monto Total</span>
              <span className="dc-summary-value">{formatCurrency(stats.totalAmount)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
